import { Component, inject, input, OnInit, output } from '@angular/core';
import { ErrorComponent, FormFieldComponent } from "@progress/kendo-angular-inputs";
import { LabelComponent } from "@progress/kendo-angular-label";
import { MultiSelectComponent } from "@progress/kendo-angular-dropdowns";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { IGroup, IUser } from "../../interfaces/i-user-profile";
import { AlertEnum } from "../alert/alert.enum";
import { ApiService } from "../../services/api.service";
import { AlertService } from "../alert/alert.service";
import { ProfileService } from "../../services/profile.service";

/**
 * Component to manage owners, users and groups in a form.
 * It uses a multi-select dropdown for owners, users, and groups and handles filtering from the API based on user input.
 *
 * The component needs to be used within a parent form and expects the parent form to have controls for 'owners', 'users', and 'groups'.
 * Since it uses the parent's form, it can handle validation and submission as part of the parent form.
 * The owners control is initialized with the current user if it hasn't been initialized by the parent form, and is always required.
 * This component is designed to be reusable and can be used in various parts of the application where user and group
 * management is needed; usually in security or access control scenarios.
 *
 * Any initialization of the values for owners, users, and groups should be done in the parent component.
 */
@Component({
  selector: 'users-groups-controller',
  imports: [
    ErrorComponent,
    FormFieldComponent,
    LabelComponent,
    MultiSelectComponent,
    ReactiveFormsModule
  ],
  templateUrl: './users-groups-controller.component.html',
  styleUrl: './users-groups-controller.component.scss'
})
export class UsersGroupsControllerComponent implements OnInit {
  parentForm = input.required<FormGroup>();
  owners: IUser[] = [];
  users: IUser[] = [];
  groups: IGroup[] = [];
  currentUser: IUser | null = null; // Store currentUser as a class property
  ownersChanged = output<IUser[]>();

  #apiService = inject(ApiService);
  #alertService = inject(AlertService);
  #profileService = inject(ProfileService);

  constructor() {
  }

  ngOnInit(): void {
    const userProfile = this.#profileService.userProfile();
    this.currentUser = {
      userId: userProfile?.profile.email || '',
      name: userProfile?.profile.name || '',
    };

    // Ensure controls exist in parentForm (if not already added)
    if (!this.parentForm().get('owners')) {
      this.parentForm().addControl('owners', new FormControl<IUser[]>([], [Validators.required]));
    }
    if (!this.parentForm().get('users')) {
      this.parentForm().addControl('users', new FormControl<IUser[]>([]));
    }
    if (!this.parentForm().get('groups')) {
      this.parentForm().addControl('groups', new FormControl<IGroup[]>([]));
    }
    // Always use parent form's values, except for owners: set to [currentUser] if empty
    const ownersValue = this.parentForm().get('owners')?.value;
    if (!ownersValue || ownersValue.length === 0) {
      this.parentForm().get('owners')?.setValue([this.currentUser]);
      this.owners = [this.currentUser];
    } else {
      this.owners = ownersValue;
    }

    this.users = this.parentForm().get('users')?.value ?? [];
    this.groups = this.parentForm().get('groups')?.value ?? [];

    // Emit ownersChanged whenever owners control value changes
    this.parentForm().get('owners')?.valueChanges.subscribe((owners: IUser[]) => {
      this.ownersChanged.emit(owners);
    });
  }

  /**
   * Filter the owners based on the value entered the owners input.
   */
  onFilterOwners(value: string) {
    if (value) {
      this.#getUsers('owners', value);
    }
  }

  /**
   * Filter the users based on the value entered the users input.
   */
  onFilterUsers(value: string) {
    if (value) {
      this.#getUsers('users', value);
    }
  }

  /**
   * Filter the users based on the value entered the users input.
   */
  onFilterGroups(value: string) {
    if (value) {
      this.#getGroups(value);
    }
  }

  /**
   * Get the users based on the type and value.
   */
  #getUsers(type: 'owners' | 'users', value: string) {
    this.#apiService.searchUsers({ criteria: value }).subscribe({
      next: (users: IUser[]) => {
        if (type === 'users') {
          this.users = users;
        } else {
          const selected = (this.parentForm().get('owners')?.value || []) as IUser[];
          // Merge API users with selected owners (avoid duplicates)
          const mergedOwners = [
            ...selected.filter(sel => !users.some(u => u.userId === sel.userId)),
            ...users
          ];
          // Ensure currentUser is present if previously selected
          const hasCurrentUser = !!this.currentUser && selected.some(u => u && this.currentUser && u.userId === this.currentUser.userId);
          if (hasCurrentUser && this.currentUser && !mergedOwners.some(u => u.userId === this.currentUser!.userId)) {
            mergedOwners.unshift(this.currentUser);
          }
          this.owners = mergedOwners;
          // Keep all previously selected owners selected
          this.parentForm().get('owners')?.setValue(selected);
        }
      },
      error: (error: any) => {
        this.#alertService.showAlert({
          message: `Failed to fetch ${type}.`,
          type: AlertEnum.error,
        });
      },
    });
  }

  /**
   * Fetches groups from the API based on the provided search criteria.
   * Updates the local groups array with the results.
   *
   * @param value - The search string used to filter groups from the API.
   */
  #getGroups(value: string) {
    this.#apiService.searchGroups({ criteria: value }).subscribe({
      next: (groups: IGroup[]) => {
        this.groups = groups;
      },
      error: (error: any) => {
        this.#alertService.showAlert({
          message: `Failed to fetch group.`,
          type: AlertEnum.error,
        });
      },
    });
  }
}
